/**
 * EL PARTE CON ID FIJO para los apuntes del gestor (auditoría 23-09 ciclo#5).
 *
 * «+ Apuntar muestreo» (`useManualSampling`) y «Añadir actualización» / «Ya no hay»
 * (`useFocusUpdate`) insertan el parte pendiente y DESPUÉS lo confirman. Si lo segundo
 * fallaba (red del invernadero, candado dos veces, OUT_OF_SCOPE…), el diálogo seguía
 * abierto y el siguiente «Guardar» insertaba OTRO parte con OTRO `clientRequestId`: un
 * parte pendiente huérfano por intento y, si la RPC sí había grabado y solo se perdió la
 * respuesta, un segundo foco en el mismo sitio.
 *
 * Lo mismo que F27 hizo en el asistente del trabajador (`pendingSessionIdRef`): el id del
 * parte y el `clientRequestId` se fijan una vez y valen para todos los reintentos del
 * MISMO apunte (mismo sitio, o mismo foco), hasta que sale bien. Reintentar es seguir con
 * ese parte: el INSERT choca (23505) y se continúa con el que ya está; las fotos ya
 * subidas no se vuelven a subir; y la RPC recibe el mismo `clientRequestId`, que es por lo
 * que deduplica un foco nuevo.
 */
import { supabase } from "@/integrations/supabase/client";
import { compressImageForAnalysis } from "@/lib/imageCompression";
import { mimeToExt, type AudioTake } from "@/hooks/useAudioRecorder";

const BUCKET = "scouting-photos";

export interface PendingPin {
  /** Qué apunte es: el sitio del «+» o el foco de «Añadir actualización». */
  key: string;
  sessionId: string;
  clientRequestId: string;
  /** Lo ya subido en un intento anterior (misma foto o mismo audio → misma URL). */
  uploaded: Map<Blob, string>;
  /** Número de la siguiente foto: nunca se pisa una que ya está en el parte. */
  nextPhoto: number;
}

/** El mismo apunte sigue con su parte; otro apunte empieza uno nuevo. */
export function pinFor(current: PendingPin | null, key: string): PendingPin {
  if (current && current.key === key) return current;
  return { key, sessionId: crypto.randomUUID(), clientRequestId: crypto.randomUUID(), uploaded: new Map(), nextPhoto: 1 };
}

/** Lo que había del parte cuando el intento anterior ya lo había insertado. */
export interface ExistingSession {
  triage_status: string | null;
  dismissal_reason: string | null;
  focus_id: string | null;
}

export type PinnedInsert =
  | { ok: true; existing: ExistingSession | null; stale: false }
  /** `stale`: el parte de ese id ya no sirve (alguien lo descartó): hay que empezar otro. */
  | { ok: false; existing: null; stale: boolean };

/**
 * Inserta el parte con su id fijo. Si ya estaba (23505: el intento anterior lo insertó, aunque
 * se perdiera la respuesta), sigue con él; si aún está pendiente, le pone lo que se haya cambiado
 * entre intentos (`onRetry`, p. ej. la nota), como hace el asistente con el sitio.
 */
export async function insertPinned(sessionId: string, payload: Record<string, unknown>, onRetry: Record<string, unknown> = {}): Promise<PinnedInsert> {
  const ins = await supabase
    .from("pest_scouting_sessions")
    .insert({ ...payload, id: sessionId } as never)
    .select("id")
    .single();
  if (!ins.error) return { ok: true, existing: null, stale: false };
  // 23505 = ya existe. Cualquier otro error es un fallo de verdad (red, permisos…).
  if ((ins.error as { code?: string }).code !== "23505") return { ok: false, existing: null, stale: false };
  const { data } = await supabase
    .from("pest_scouting_sessions")
    .select("id, triage_status, dismissal_reason, focus_id")
    .eq("id", sessionId)
    .maybeSingle();
  if (!data) return { ok: false, existing: null, stale: false };
  const existing = data as unknown as ExistingSession;
  // Descartado a mano entretanto (y no como revisión limpia): ese parte ya no es este apunte.
  if (existing.triage_status === "dismissed" && existing.dismissal_reason !== "clean_check") return { ok: false, existing: null, stale: true };
  if (existing.triage_status === "pending_triage" && Object.keys(onRetry).length > 0) {
    try {
      await supabase.from("pest_scouting_sessions").update(onRetry as never).eq("id", sessionId);
    } catch {
      /* lo del primer intento sigue valiendo */
    }
  }
  return { ok: true, existing, stale: false };
}

/**
 * Sube las fotos y el audio que falten, cada uno en su try/catch: si una subida falla, el parte
 * sigue valiendo (y el siguiente intento la vuelve a probar). Lo ya subido no se repite.
 */
export async function uploadPinnedMedia(
  pin: PendingPin,
  companyId: string,
  photos: readonly File[],
  audio: AudioTake | null,
): Promise<{ urls: string[]; audioUrl: string | null }> {
  const urls: string[] = [];
  for (const file of photos) {
    const ya = pin.uploaded.get(file);
    if (ya) {
      urls.push(ya);
      continue;
    }
    try {
      const { file: compressed } = await compressImageForAnalysis(file);
      const ext = compressed.type.includes("png") ? "png" : "jpg";
      const path = `${companyId}/${pin.sessionId}/photo-${pin.nextPhoto++}.${ext}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, compressed, { upsert: true, contentType: compressed.type || "image/jpeg" });
      if (error) continue;
      const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
      pin.uploaded.set(file, url);
      urls.push(url);
    } catch {
      /* se sigue sin esa foto */
    }
  }
  let audioUrl: string | null = null;
  if (audio) {
    const ya = pin.uploaded.get(audio.blob);
    if (ya) audioUrl = ya;
    else {
      try {
        const path = `${companyId}/${pin.sessionId}/audio.${mimeToExt(audio.mime)}`;
        const { error } = await supabase.storage.from(BUCKET).upload(path, audio.blob, { upsert: true, contentType: audio.mime || "audio/webm" });
        if (!error) {
          audioUrl = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
          pin.uploaded.set(audio.blob, audioUrl);
        }
      } catch {
        /* se sigue sin el audio */
      }
    }
  }
  return { urls, audioUrl };
}

/** Deja en el parte las fotos y el audio; si falla, el parte ya está y no es fatal. */
export async function saveMedia(sessionId: string, media: { urls: string[]; audioUrl: string | null }): Promise<void> {
  if (!media.urls.length && !media.audioUrl) return;
  try {
    await supabase
      .from("pest_scouting_sessions")
      .update({ photo_urls: media.urls.length ? media.urls : null, photo_url: media.urls[0] ?? null, audio_url: media.audioUrl })
      .eq("id", sessionId);
  } catch {
    /* el parte ya está; las fotos y el audio no son fatales */
  }
}
